import React, { createContext, FC, PropsWithChildren, ReactElement, useMemo, useState } from "react";

import { Alert, AlertColor, Snackbar, SnackbarOrigin } from "@mui/material";

export interface ToastProps {
    message: string | ReactElement;
    severity: AlertColor;
    anchorOrigin?: SnackbarOrigin;
}

type IToastContext = {
    openToast: (options: ToastProps) => void;
    closeToast: () => void;
};

export const ToastContext = createContext({ openToast: null } as IToastContext);
ToastContext.displayName = "ToastContext";

export const ToastProvider: FC<PropsWithChildren> = ({ children }) => {
    const [toast, setToast] = useState<ToastProps>({
        severity: null,
        message: null,
        anchorOrigin: null,
    });
    const [open, setOpen] = useState(false);

    const closeToast = () => setOpen(false);

    const openToast = (options: ToastProps) => {
        setToast(options);
        setOpen(true);
    };

    const contextValue = useMemo(() => ({ openToast, closeToast }), []);

    return (
        <ToastContext.Provider value={contextValue}>
            <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={closeToast}
                anchorOrigin={toast.anchorOrigin || { vertical: "top", horizontal: "center" }}
                data-testid="toast-active"
            >
                <div>
                    <Alert onClose={closeToast} severity={toast.severity} style={{ maxWidth: "350px" }} elevation={6}>
                        {toast.message}
                    </Alert>
                </div>
            </Snackbar>
            {children}
        </ToastContext.Provider>
    );
};
